/* ==========================================================================================
   MES BRACELET BUILDER v2 — LOGIC CORE (H296)
   Woz | Developer, 27 Aug 2026.

   The state machine and the arithmetic. No DOM, no rendering: this file is the part the
   handover calls "must match exactly", kept separate so it can be reasoned about and tested
   on its own. The render layer lives in mes-bracelet-builder.js. ONE exception (H500): the
   ring drawing, drawPreview(), lives here so the builder and Mesmerize OS draw a bracelet with
   the same code; it touches only the container it is handed.

   Ported from the design prototype `builder/Bracelet Builder Mobile v2.dc.html`, which H296
   names the source of truth for logic. Where this file DEPARTS from the prototype it is
   marked ⛔ and the reason is stated — every departure is a defect the ticket asked me to fix.
   ========================================================================================== */
(function (root) {
  'use strict';

  /* ---------------------------------------------------------------------------------------
     CATALOGUE — injected by the section from library/baselines/bracelet-builder/*.json,
     then OVERRIDDEN at runtime by Shopify metafields (H297). The JSON is the launch
     snapshot; it is never the price authority.
     --------------------------------------------------------------------------------------- */
  var CAT = { stones: [], sizes: [], hardware: null, byId: {} };

  function loadCatalogue(data) {
    /* ⛔ Probe-safe: called with NO argument this is a GETTER, never a reset. Measured 29 Aug:
       a diagnostic `loadCatalogue()` call from the console wiped the live page's catalogue to
       empty and made every later probe read zeros - the probe manufactured the defect it then
       reported. A function that looks like a getter must not destroy state when used as one. */
    if (data === undefined) { return CAT; }
    CAT.stones = (data && data.stones) || [];
    CAT.sizes = (data && data.sizes) || [];
    CAT.hardware = (data && data.hardware) || null;
    CAT.byId = {};
    CAT.stones.forEach(function (s) { CAT.byId[s.id] = s; });
    return CAT;
  }

  /* ---------------------------------------------------------------------------------------
     ⛔ PRICE AUTHORITY — a failed metafield fetch must be LOUD, not a silent fallback.

     The snapshot exists so the page still renders if the metafield fetch fails. It must NOT
     silently become the price authority, and the reason is specific rather than theoretical:
     on 27 Aug Shrey raised the per-bead floor ₹39 → ₹49, and the design bundle's own
     data/stones.json is stale on it TODAY. A stale snapshot prices a build LOW, rounds it into
     a LOWER bucket, and adds a CHEAPER pre-priced variant — so the customer is charged less
     than the bracelet costs and nothing anywhere reports it. Revenue walks out silently.

     CLAUDE.md §0d: an operation that can succeed emptily is assumed to have done so. So the
     authority is recorded, and the render layer refuses add-to-cart while it reads 'snapshot'.
     A page that cannot take an order for a few seconds beats one that sells at the wrong price.
     --------------------------------------------------------------------------------------- */
  var priceAuthority = 'snapshot';

  function getPriceAuthority() { return priceAuthority; }

  /* Runtime price/stock from metafields. Absent key = leave the snapshot value alone;
     absent STOCK = in stock. Hiding a stone is the caller's job (grid filter), not ours —
     but a bead already placed with a now-out-of-stock stone must still price correctly,
     which is why stock never touches price. */
  function applyRuntime(rows) {
    if (!rows || !rows.length) { return { priced: 0, hidden: 0, authority: priceAuthority }; }
    var priced = 0, hidden = 0;
    rows.forEach(function (r) {
      var s = CAT.byId[r.id];
      if (!s) { return; }
      if (typeof r.priceINR === 'number' && r.priceINR >= 0) { s.priceINR = r.priceINR; priced++; }
      if (r.inStock === false) { s.outOfStock = true; hidden++; }
      else if (r.inStock === true) { s.outOfStock = false; }
    });
    /* Authority flips only when the fetch actually priced something. A response that arrived
       but carried no usable rows is the same as no response for this purpose — it is the
       "plausible value instead of an error" case, and it must not read as success. */
    if (priced > 0) { priceAuthority = 'runtime'; }
    return { priced: priced, hidden: hidden, authority: priceAuthority };
  }

  function inStockStones() {
    return CAT.stones.filter(function (s) { return !s.outOfStock; });
  }

  /* ---------------------------------------------------------------------------------------
     OWNER SWITCHES (V9.28.116, Shrey A764 = 1A, 29 Sep 2026): "how do we add or remove stones when
     they come in and out of stock? Same for the MagSnap closure and all the other options."
     Stones: `inStock` per stone in the shop metafield `custom.bracelet_stone_catalogue` (unchanged).
     Everything else: the SAME metafield's optional `_off` list, e.g. "_off": ["elastic", "gold", "om", "luxury"].
     Ids: closures magsnap · elastic | finishes silver · gold · black · gloss · matte (or ONE pair, "black-gloss") | centre pieces om ·
     trishul · evileye · chakra · spinel · core_goldhematite · core_evileye · core_omonyx | initials cube: cube |
     boxes kappa (Black Box) · luxury. A missing `_off` means everything is on.
     normalise() enforces both on EVERY path (a tap, a preset, a Customize-it or shared link, a saved design),
     so nothing switched off or out of stock can reach the cart: out-of-stock beads become empty slots (the
     count is left on st._clearedOos for the page to say so), and a switched-off option falls back to one
     that is on. */
  var OFF = {};
  function applyAvailability(list) {
    OFF = {};
    (Array.isArray(list) ? list : []).forEach(function (id) {
      if (typeof id === 'string' && id.trim()) { OFF[id.trim().toLowerCase()] = true; }
    });
  }
  function isOn(id) { return !OFF[String(id == null ? '' : id).toLowerCase()]; }
  /* V9.28.125 (Shrey, 30 Sep: "we don't have a gloss black MagSnap right now"): ONE finish combination can be off
     without touching its colour or its surface elsewhere. `_off` takes "<colour>-<surface>", e.g. "black-gloss". */
  function finishOn(color, surface) { return isOn(color) && isOn(surface) && isOn(color + '-' + surface); }
  function stoneOk(id) { var s = CAT.byId[id]; return !!s && !s.outOfStock; }
  function firstOn(ids) { for (var i = 0; i < ids.length; i++) { if (isOn(ids[i])) { return ids[i]; } } return null; }
  function enforceAvailability(st) {
    var cleared = 0;
    if (st.beads && st.beads.length) {
      st.beads = st.beads.map(function (b) {
        if (b && CAT.byId[b] && CAT.byId[b].outOfStock) { cleared++; return null; }
        return b;
      });
    }
    if (cleared) { st._clearedOos = (st._clearedOos || 0) + cleared; }
    if (st.palette && st.palette.length) { st.palette = st.palette.filter(stoneOk); }
    if (st.construction && !isOn(st.construction)) {
      var nextC = firstOn(st.construction === 'magsnap' ? ['elastic'] : ['magsnap']);
      if (nextC && st.size && st.beads && st.beads.length) {
        var want = beadCount(st.size, nextC);
        if (want && want !== st.beads.length) { st.beads = adjustTo(want, st.beads, st.mirror); }
      }
      st.construction = nextC;
    }
    var colors = (CAT.hardware && CAT.hardware.colors || []).map(function (c) { return c.id; });
    if (st.color && !isOn(st.color)) { st.color = firstOn(colors) || st.color; }
    if (st.surface && !isOn(st.surface)) { st.surface = firstOn(st.surface === 'gloss' ? ['matte'] : ['gloss']) || st.surface; }
    if (st.color && st.surface && !finishOn(st.color, st.surface)) {
      /* keep the colour and flip the surface if that pair exists; otherwise keep the surface and move the colour */
      var otherS = st.surface === 'gloss' ? 'matte' : 'gloss';
      if (finishOn(st.color, otherS)) { st.surface = otherS; }
      else {
        for (var ci = 0; ci < colors.length; ci++) { if (finishOn(colors[ci], st.surface)) { st.color = colors[ci]; break; } }
      }
    }
    if (st.charm && st.charm !== 'none' && !isOn(st.charm)) { st.charm = 'none'; }
    if (st.cubeOn && !isOn('cube')) { st.cubeOn = false; }
    if ((st.centreMode === 'cube' || st.centreMode === 'personalised') && !isOn('cube')) { st.centreMode = 'core'; }
    if (st.box && !isOn(st.box)) { st.box = firstOn(st.box === 'luxury' ? ['kappa'] : ['luxury']) || st.box; }
    return st;
  }

  /* ---------------------------------------------------------------------------------------
     BEAD COUNT
     --------------------------------------------------------------------------------------- */
  function sizeRow(sizeId) {
    for (var i = 0; i < CAT.sizes.length; i++) { if (CAT.sizes[i].id === sizeId) { return CAT.sizes[i]; } }
    return null;
  }

  function beadCount(sizeId, construction) {
    var z = sizeRow(sizeId);
    if (!z) { return 0; }
    return construction === 'elastic' ? z.beadsElastic : z.beadsMagSnap;
  }

  /* ---------------------------------------------------------------------------------------
     CENTRE PIECE — ⛔ DEPARTURE 1 (H296 finding 2)

     The prototype allows an initials cube AND a silver charm at once:
         cubeIndex  = st.cubeOn ? topIndex : -1;
         charmIndex = st.charm !== "none" ? (st.cubeOn ? topIndex + 1 : topIndex) : -1;
     ...and total() adds both prices. The design presents them as TABS, so the UI makes it
     look one-or-the-other, and the verification pass recorded exclusivity as "held
     structurally". It is not held in the state.

     That was harmless in a design file. It stops being harmless here, because this ticket
     requires the share code and the autosave record to carry centre state — and BOTH write
     state without going near a tab. So exclusivity is enforced in normalise(), which every
     entry path runs through: decode, restore, preset and UI alike.
     --------------------------------------------------------------------------------------- */
  function normalise(st) {
    if (st.cubeOn && st.charm && st.charm !== 'none') {
      /* Last writer wins, and the caller says which that was. Without a hint we keep the
         charm: it is the dearer, more deliberate choice and the one a shared link is more
         likely to have meant. */
      if (st._lastCentre === 'cube') { st.charm = 'none'; }
      else { st.cubeOn = false; }
    }
    /* Initials are kept even while the cube is OFF - the design's flow is type-first,
       then "Add cube", and clearing them here made that button permanently dead (measured
       29 Aug driving the rebuilt UI). They are harmless un-added: encodeBuild only carries
       them when cubeOn is true, so they can never leak into _build or a share link. */
    if (st.initials) { st.initials = String(st.initials).toUpperCase().slice(0, 2); }
    delete st._lastCentre;
    enforceAvailability(st);
    return st;
  }

  function centreIndices(st, n) {
    var top = Math.floor(n / 2);
    /* After normalise() at most one of these is ever set, so they can no longer both
       consume a bead the way topIndex/topIndex+1 did. */
    return {
      cube: st.cubeOn ? top : -1,
      charm: (st.charm && st.charm !== 'none') ? top : -1,
      top: top
    };
  }

  /* ---------------------------------------------------------------------------------------
     PRICE — the prototype's total(), ported exactly.

     ⛔ The centre piece OCCUPIES a bead and that bead's stone price is EXCLUDED. This is
     what makes the true ceiling ₹20,371 rather than the ₹21,070 quoted in the handover, and
     the band 775 buckets rather than ~804.
     → reports/bracelet-builder/2026-08-27-price-band-and-centre-piece.md
     --------------------------------------------------------------------------------------- */
  function charmDef(id) {
    if (!id || id === 'none' || !CAT.hardware) { return null; }
    var list = CAT.hardware.charms || [];
    for (var i = 0; i < list.length; i++) { if (list[i].id === id) { return list[i]; } }
    return null;
  }

  function priceOf(st) {
    var n = st.beads.length;
    var ci = centreIndices(st, n);
    var cd = charmDef(st.charm);
    var hw = CAT.hardware || {};
    var stoneCost = 0, stoneCount = 0;

    st.beads.forEach(function (id, i) {
      if (i === ci.cube || i === ci.charm || !id) { return; }
      var s = CAT.byId[id];
      if (!s) { return; }
      stoneCount++;
      stoneCost += s.priceINR;
    });

    var closure = 0;
    if (st.construction === 'magsnap') { closure = (hw.closures && hw.closures.magsnap.priceINR) || 0; }
    else if (st.construction === 'elastic') { closure = (hw.closures && hw.closures.elastic.priceINR) || 0; }

    var cube = st.cubeOn ? ((hw.initialsCube && hw.initialsCube.priceINR) || 0) : 0;
    var charm = cd ? cd.priceINR : 0;
    var box = 0;
    if (st.box === 'luxury' && hw.boxes) {
      hw.boxes.forEach(function (b) { if (b.id === 'luxury') { box = b.priceINR; } });
    }

    var trueTotal = stoneCost + closure + cube + charm + box;
    return {
      stoneCost: stoneCost,
      stoneCount: stoneCount,
      closure: closure,
      cube: cube,
      charm: charm,
      box: box,
      trueTotal: trueTotal,
      bucket: bucketFor(trueTotal)
    };
  }

  /* ---------------------------------------------------------------------------------------
     BUCKETS — ⛔ the bounds are COMPUTED from the catalogue, never typed.

     H296: "Generate the bucket bounds from the configurator's own computed min/max — never a
     typed constant. The band has moved three times in two days; a hard-coded ceiling silently
     creates builds with no variant to land in."

     So band() walks every size × closure × centre-piece combination using the SAME price
     function the page uses, which means the bounds cannot drift away from what the page can
     actually produce.
     --------------------------------------------------------------------------------------- */
  var STEP = 25;

  function bucketFor(trueTotal) {
    return Math.ceil(trueTotal / STEP) * STEP;
  }

  function band() {
    var live = inStockStones();
    if (!live.length || !CAT.sizes.length || !CAT.hardware) { return null; }
    var cheapest = live.reduce(function (a, s) { return s.priceINR < a ? s.priceINR : a; }, Infinity);
    var dearest = live.reduce(function (a, s) { return s.priceINR > a ? s.priceINR : a; }, 0);
    var hw = CAT.hardware;
    var dearestCharm = (hw.charms || []).reduce(function (a, c) { return c.priceINR > a ? c.priceINR : a; }, 0);
    var cubePrice = (hw.initialsCube && hw.initialsCube.priceINR) || 0;
    var luxBox = (hw.boxes || []).reduce(function (a, b) { return b.priceINR > a ? b.priceINR : a; }, 0);

    var lo = Infinity, hi = 0, hiAt = null;
    CAT.sizes.forEach(function (z) {
      [['magsnap', z.beadsMagSnap], ['elastic', z.beadsElastic]].forEach(function (pair) {
        var fit = pair[0], n = pair[1];
        var closure = fit === 'elastic' ? hw.closures.elastic.priceINR : hw.closures.magsnap.priceINR;
        /* floor: every bead the cheapest stone, no centre piece, signature box */
        var f = closure + n * cheapest;
        if (f < lo) { lo = f; }
        /* ceiling: exclusivity means at most ONE centre slot, so exactly two candidates */
        [
          { occ: 0, extra: 0 },
          { occ: 1, extra: dearestCharm },
          { occ: 1, extra: cubePrice }
        ].forEach(function (c) {
          var t = closure + (n - c.occ) * dearest + c.extra + luxBox;
          if (t > hi) { hi = t; hiAt = { size: z.id, fit: fit, centre: c.extra === dearestCharm ? 'charm' : (c.extra ? 'cube' : 'none') }; }
        });
      });
    });

    var loB = bucketFor(lo), hiB = bucketFor(hi);
    return {
      trueMin: lo, trueMax: hi, maxAt: hiAt,
      minBucket: loB, maxBucket: hiB, step: STEP,
      variantCount: ((hiB - loB) / STEP) + 1
    };
  }

  /* ---------------------------------------------------------------------------------------
     MIRROR + AUTO-FILL — ported from the prototype unchanged.
     --------------------------------------------------------------------------------------- */
  function isSymmetric(b) {
    var n = b.length;
    for (var i = 0; i < Math.floor(n / 2); i++) {
      if ((b[i] || null) !== (b[n - 1 - i] || null)) { return false; }
    }
    return true;
  }

  function placeBead(beads, i, stoneId, mirror) {
    var out = beads.slice(), n = out.length;
    out[i] = stoneId;
    if (mirror) { out[n - 1 - i] = stoneId; }
    return out;
  }

  /* Auto-advance stops AT the centre bead rather than wrapping — the odd bead count exists
     so that centre is a real position and not a rounding artefact. */
  function nextIndex(beads, from, mirror) {
    var n = beads.length, half = Math.ceil(n / 2);
    var limit = mirror ? half : n;
    for (var i = from + 1; i < limit; i++) { if (!beads[i]) { return i; } }
    for (var j = 0; j < limit; j++) { if (!beads[j]) { return j; } }
    return -1;
  }

  function autoFill(beads, mirror) {
    var n = beads.length;
    var seq = mirror ? beads.slice(0, Math.ceil(n / 2)).filter(Boolean) : beads.filter(Boolean);
    if (!seq.length) { seq = beads.filter(Boolean); }
    if (!seq.length) { return beads.slice(); }
    var out = new Array(n), i;
    if (mirror) {
      var half = Math.ceil(n / 2);
      for (i = 0; i < half; i++) { out[i] = seq[i % seq.length]; out[n - 1 - i] = out[i]; }
    } else {
      for (i = 0; i < n; i++) { out[i] = seq[i % seq.length]; }
    }
    return out;
  }

  /* ---------------------------------------------------------------------------------------
     RESIZE — shrinkPairs + adjustTo, ported from the prototype unchanged.
     Removing beads keeps the design symmetric and clear of the clasp ends and the centre.
     --------------------------------------------------------------------------------------- */
  function shrinkPairs(n, beadsIn) {
    var out = beadsIn.slice();
    var k = (out.length - n) / 2, i;
    for (i = Math.floor(out.length / 2) - 1; i >= 0 && k; i--) {
      if (!out[i] && !out[out.length - 1 - i]) {
        out.splice(out.length - 1 - i, 1);
        out.splice(i, 1);
        k--;
      }
    }
    if (!k) { return out; }
    var len = out.length, half = Math.floor(len / 2);
    var count = {};
    out.forEach(function (b) { if (b) { count[b] = (count[b] || 0) + 1; } });
    var rarity = function (b) { return b ? (len - (count[b] || 0)) / len : 0; };
    var lo = 1, hi = Math.max(1, half - k - 1);
    /* V9.28.124 (H454): an ALTERNATING end (repeat 2 or 3 over the outer beads) may shrink from the clasp ends. Every
       inner cut of "A B A B A" leaves "A B B A"; dropping the end bead leaves "B A B A", still alternating. Measured:
       Beauty Pearl and Spiritual Pyrite Om (alternating halves around a centre) broke at S until this. A plain run or a
       non-repeating end keeps lo = 1, as before. */
    if (endPeriod(out.slice(0, Math.min(6, half))) > 1) { lo = 0; }
    var bestS = lo, bestCost = Infinity;
    for (var s = lo; s <= hi; s++) {
      var cost = 0;
      for (var j = s; j < s + k; j++) { cost += 3 * rarity(out[j]); }
      /* does the seam left behind still read like the original pattern (run vs alternation)? */
      if (s > 0 && (out[s - 1] === out[s]) !== (out[s - 1] === out[s + k])) { cost += 1.2; }
      if (cost < bestCost) { bestCost = cost; bestS = s; }
    }
    out.splice(len - bestS - k, k);
    out.splice(bestS, k);
    return out;
  }

  /* V9.28.96 (H402): the smallest repeat of a FULLY filled design (2 to half its length), or 0. Uniform designs
     (repeat 1) and anything with a gap or centre piece return 0 and keep the older behaviour. V9.28.99: up to HALF,
     not a third, so a 7-stone chakra cycle on 19 beads counts (Multi Chakra broke its sequence at S/XL/L, measured). */
  function patternPeriod(b) {
    if (!b.length || b.some(function (x) { return !x; })) { return 0; }
    for (var p = 2; p <= Math.floor(b.length / 2); p++) {
      var ok = true;
      for (var q = p; q < b.length && ok; q++) { if (b[q] !== b[q - p]) { ok = false; } }
      if (ok) { return p; }
    }
    return 0;
  }

  /* the smallest repeat (1 to 3) of a short gap-free end segment, or 0 */
  function endPeriod(seg) {
    if (!seg.length || seg.some(function (x) { return !x; })) { return 0; }
    for (var p = 1; p <= 3 && p < seg.length; p++) {
      var ok = true;
      for (var q = p; q < seg.length && ok; q++) { if (seg[q] !== seg[q - p]) { ok = false; } }
      if (ok) { return p; }
    }
    return 0;
  }

  function adjustTo(n, beadsIn, mirror) {
    var out = beadsIn.slice(), i;
    /* V9.28.99: a repeating design SHRINKS from its two ends, so the repeat stays unbroken (shrinkPairs removes an
       inner band, which cut the chakra cycle twice at S). Growing is handled below with the same period. */
    var shrinkPeriod = n < out.length ? patternPeriod(out) : 0;
    if (shrinkPeriod) {
      var cut = out.length - n, headCut = Math.floor(cut / 2);
      return out.slice(headCut, headCut + n);
    }
    /* V9.28.98 (H402): even shrinks trim in symmetric pairs WHATEVER the mirror setting. The non-mirror path below drops
       empty slots first, and a centre charm's slot IS empty, so a mirror-off design lost its centre gap and a stone went
       under the charm (Super 11 Chakra at S lost Lapis, measured 28 Sep). shrinkPairs never touches the centre. */
    if (n < out.length && (out.length - n) % 2 === 0) { return shrinkPairs(n, out); }
    /* Growing a repeating design (e.g. alternating Red Tiger / Onyx) continues the repeat at both ends; padding
       with copies of the end beads turned RORO…R into RRRORO…RRR (measured 27 Sep, L and XL). */
    var period = n > out.length ? patternPeriod(out) : 0;
    if (period) {
      var add = n - out.length, headN = Math.ceil(add / 2);
      for (i = 0; i < headN; i++) { out.unshift(out[period - 1]); }
      for (i = 0; i < add - headN; i++) { out.push(out[out.length - period]); }
      return out;
    }
    if (n > out.length) {
      /* V9.28.104: each END continues its own local repeat (1 to 3 beads over its outer 6), so alternating ends on a
         design with a centre charm stay alternating: Rudraksh Om at XL used to start "Lava, Lava, Lava" (measured 28 Sep).
         A plain run (repeat 1) just gets longer, exactly as before; an end with no repeat falls back to copying its bead. */
      var k = n - out.length, head = Math.ceil(k / 2);
      var span = Math.min(6, Math.floor(out.length / 2));
      var lp = endPeriod(out.slice(0, span)), rp = endPeriod(out.slice(out.length - span));
      for (i = 0; i < head; i++) { out.unshift(lp ? out[lp - 1] : (out[0] || null)); }
      for (i = 0; i < k - head; i++) { out.push(rp ? out[out.length - rp] : (out[out.length - 1] || null)); }
    } else if (n < out.length) {
      var need = out.length - n;
      for (i = out.length - 1; i >= 0 && need; i--) { if (!out[i]) { out.splice(i, 1); need--; } }
      while (need > 0) {
        var freq = {};
        out.forEach(function (b) { if (b) { freq[b] = (freq[b] || 0) + 1; } });
        var bestI = 0, bestF = -1;
        for (var k2 = 0; k2 < out.length; k2++) {
          var idx = k2 % 2 ? out.length - 1 - ((k2 / 2) | 0) : (k2 / 2) | 0;
          var b = out[idx];
          var f = b ? (freq[b] || 0) : 0;
          if (f > bestF) { bestF = f; bestI = idx; }
        }
        out.splice(bestI, 1);
        need--;
      }
    }
    return out;
  }

  /* ---------------------------------------------------------------------------------------
     SHARE / _build CODEC — ⛔ DEPARTURE 2 (H296 finding 1, the production defect)

     The prototype tokenised a bead as the first THREE LETTERS of the stone id. `redjasper`
     and `redtiger` both become `red`. That token goes into `_build`, and `_build` is what the
     WORKSHOP READS TO STRING THE BRACELET — so a collision means the wrong stone is
     assembled and nothing on the order record reveals it. Full ids, always.

     The prototype also dropped centre piece, initials and box from the code, so a shared
     link and the ops record both lost paid add-ons. All three are carried here.

     Format (URL-encoded, same string for the share link and the _build property):
       d=<id>-<id>-_-<id>&c=<construction>&f=<color>-<surface>&s=<size>
       &centre=<charmId|cube>&init=<AB>&metal=<silver|gold>&box=<kappa|luxury>&m=<1|0>
     `_` is an empty bead. Unknown ids decode to an empty bead rather than throwing: a
     stale link must degrade to a gap the customer can see and fill, never to a wrong stone.
     --------------------------------------------------------------------------------------- */
  var EMPTY = '_';

  function encodeBuild(st) {
    var parts = [];
    parts.push('d=' + st.beads.map(function (b) { return b || EMPTY; }).join('-'));
    if (st.construction) { parts.push('c=' + st.construction); }
    if (st.construction === 'magsnap') { parts.push('f=' + (st.color || 'silver') + '-' + (st.surface || 'gloss')); }
    if (st.size) { parts.push('s=' + st.size); }
    if (st.charm && st.charm !== 'none') { parts.push('centre=' + st.charm); }
    else if (st.cubeOn) {
      parts.push('centre=cube');
      if (st.initials) { parts.push('init=' + String(st.initials).toUpperCase().slice(0, 2)); }
      parts.push('metal=' + (st.cubeMetal || 'silver'));
    }
    parts.push('box=' + (st.box === 'luxury' ? 'luxury' : 'kappa'));
    parts.push('m=' + (st.mirror ? 1 : 0));
    return parts.join('&');
  }

  /* V9.28.155 (H500): the parse is split from the normalise so drawPreview() can draw a SAVED build exactly as
     it was ordered - normalise() runs the owner switches and stock, which would blank a bead that has since gone
     out of stock. decodeBuild() is unchanged: parseBuild() then normalise(), as before. */
  function decodeBuild(code) {
    /* an empty code returned the defaults WITHOUT normalise before the split; kept exactly */
    if (!code) { return parseBuild(code); }
    return normalise(parseBuild(code));
  }

  function parseBuild(code) {
    var out = {
      beads: [], size: null, construction: null, color: 'silver', surface: 'gloss',
      charm: 'none', cubeOn: false, initials: '', cubeMetal: 'silver',
      box: 'kappa', mirror: true, unknownBeads: 0
    };
    if (!code) { return out; }
    String(code).split('&').forEach(function (kv) {
      var i = kv.indexOf('=');
      if (i < 0) { return; }
      var k = kv.slice(0, i), v = decodeURIComponent(kv.slice(i + 1));
      if (k === 'd') {
        out.beads = v.split('-').map(function (t) {
          if (t === EMPTY || !t) { return null; }
          if (CAT.byId[t]) { return t; }
          out.unknownBeads++;
          return null;
        });
      } else if (k === 'c') { out.construction = (v === 'elastic' || v === 'magsnap') ? v : null; }
      else if (k === 'f') { var f = v.split('-'); out.color = f[0] || 'silver'; out.surface = f[1] || 'gloss'; }
      else if (k === 's') { out.size = sizeRow(v) ? v : null; }
      else if (k === 'centre') {
        if (v === 'cube') { out.cubeOn = true; out.charm = 'none'; out._lastCentre = 'cube'; }
        else if (charmDef(v)) { out.charm = v; out.cubeOn = false; out._lastCentre = 'charm'; }
      }
      else if (k === 'init') { out.initials = v.toUpperCase().slice(0, 2); }
      else if (k === 'metal') { out.cubeMetal = (v === 'gold') ? 'gold' : 'silver'; }
      else if (k === 'box') { out.box = (v === 'luxury') ? 'luxury' : 'kappa'; }
      else if (k === 'm') { out.mirror = v !== '0'; }
    });
    /* every entry path goes through normalise — decodeBuild() included (it calls this, then normalise) */
    return out;
  }

  /* ---------------------------------------------------------------------------------------
     TALLY (V9.28.117, H422 items 2+3, agreed with Linus): the stones to PICK for one build, from its
     share/`_build` string. ONE function for the production sheet and Mesmerize OS's pick lists, so the
     two can never disagree. Rules are the sheet's, unchanged: beads = `d` split on '-'; with a centre
     piece (`centre` set and not 'none', the initials cube included) the middle position floor(n/2) is the
     centre, not a stone; '_' is an empty slot; an id the catalogue does not know is kept LOUD in `unknown`
     (once per bead), never dropped. No DOM, no window: Linus runs this in a sandboxed frame.
     Returns { n, stoneCount, stones: [{ id, name, tier, count }] (count desc, then id), centre: { id, name }
     | null, unknown: [ids] }.
     --------------------------------------------------------------------------------------- */
  function tallyBuild(code) {
    var q = {};
    String(code || '').replace(/^\?/, '').split('&').forEach(function (p) {
      var i = p.indexOf('=');
      if (i > 0) { try { q[decodeURIComponent(p.slice(0, i))] = decodeURIComponent(p.slice(i + 1)); } catch (e) {} }
    });
    var beads = String(q.d || '').split('-').filter(function (x) { return x; });
    var n = beads.length;
    var hasCentre = !!(q.centre && q.centre !== 'none');
    var centreIdx = hasCentre ? Math.floor(n / 2) : -1;
    var counts = {}, unknown = [], stoneCount = 0;
    beads.forEach(function (id, i) {
      if (i === centreIdx || id === '_') { return; }
      stoneCount++;
      counts[id] = (counts[id] || 0) + 1;
      if (!CAT.byId[id]) { unknown.push(id); }
    });
    var stones = Object.keys(counts).sort(function (x, y) {
      return counts[y] - counts[x] || String(x).localeCompare(String(y));
    }).map(function (id) {
      var s = CAT.byId[id];
      return { id: id, name: s ? s.name : id, tier: s ? (s.tier || null) : null, count: counts[id] };
    });
    var centre = null;
    if (hasCentre) {
      var cd = q.centre === 'cube' ? null : charmDef(q.centre);
      centre = { id: q.centre, name: q.centre === 'cube' ? 'Initials cube' : (cd ? cd.name : q.centre) };
    }
    return { n: n, stoneCount: stoneCount, stones: stones, centre: centre, unknown: unknown };
  }

  /* ---------------------------------------------------------------------------------------
     RING PREVIEW — drawPreview() (V9.28.155, H500, Linus → Woz)

     The ONE drawing of a bracelet: the customer builder's ring AND Mesmerize OS's Production
     preview call this same function, so the two can never drift. Moved here VERBATIM out of
     the builder's renderRing() (geometry, gap solver, faces, thread, clasp); the builder keeps
     only its UI state (mirror ghost, remove mode, clasp focus, clicks), which it applies
     through opts.decorate. This is the only part of the core that touches a DOM, and it uses
     the container's own document - no window, no fetch, no globals (the OS runs it in a
     sandboxed frame).

     drawPreview(el, build, opts)
       el     positioned container; cleared, then filled with %-positioned children (square box)
       build  the share / `_build` string (encodeBuild's format), or a state object with
              beads, construction, color, surface, charm, cubeOn, cubeMetal, initials.
              ⛔ A string is PARSED, not normalised: a saved build is drawn as it was ordered,
              never re-run through today's stock and owner switches.
       opts   claspUrls / claspBase   the builder config's shape; versioned map first
              claspAspect             image height/width, default 385/900 (the design's)
              stageWidth              px width for the cube glyph; default el.clientWidth, then 300
              interactive             true = <button type=button> (the builder); else static spans
              highlight               bead index drawn with the selected-bead ring
              decorate(node, info)    called per bead and for the clasp, before it is attached:
                                      info = { kind:'bead'|'clasp', index, id, isCube, isCharm, highlighted }
       returns { placed, n, beads: [nodes], clasp: node|null, unknown }
     --------------------------------------------------------------------------------------- */
  var ACCENT = '#5d8bd7';
  var SELECT_RING = '0 0 0 2px ' + ACCENT + ', 0 0 0 5px rgba(93,139,215,0.16)';
  var CLASP_W_FIXED = 21.19793227033449;
  var BEAD_PCT_FIXED = 12;
  var BEAD_PITCH = 0.9615;
  /* ⛔ CLASP_SCALE — Shrey, 29 Aug: "the MagSnap is still extremely small", twice. The design's
     constant renders the barrel 0.86 bead-diameters THICK; the real MagSnap 4 barrel is the same
     diameter as an 8mm bead and about 2.5 bead-lengths long. Scaling is anchored to that physical
     ratio rather than to a number that looked right once: at 1.39 the render measures 1.05 bead
     thick / 2.46 bead long. The scale feeds the GAP SOLVER as well as the render — feeding only
     the render (the old ad-hoc x1.14) makes the clasp overlap the end beads, because the ring
     opens a gap sized for the unscaled clasp. Measured across all four bead counts before use. */
  var CLASP_SCALE = 1.32;  /* 1.39 -> 1.32 : Shrey, 30 Aug, "a hint smaller, very slightly" (-5%) */
  var CLASP_W_EFF = CLASP_W_FIXED * CLASP_SCALE;
  var CLASP_ASPECT_DEFAULT = 385 / 900; /* the design's own height formula uses 385/900; the builder
    reads the real image aspect on load and passes it in, so the render is never squashed */
  var COLORS = {
    silver: { name: 'Silver', a: '#F2F3F5', b: '#B6BBC2', c: '#868C95', spec: '#FFFFFF', etch: 'rgba(30,35,40,0.55)' },
    gold:   { name: 'Gold',   a: '#F0D48A', b: '#B8912F', c: '#8A6A1E', spec: '#FFF8DF', etch: 'rgba(60,40,0,0.55)' },
    black:  { name: 'Black',  a: '#4A4A50', b: '#26262A', c: '#141416', spec: '#9BA0A8', etch: 'rgba(255,255,255,0.45)' }
  };
  var CUBE_METAL = {
    silver: { label: 'Silver', grad: 'linear-gradient(145deg,#F2F3F5,#C3C8CE 48%,#8E949C)', etch: 'rgba(20,20,22,0.85)', etchOff: 'rgba(20,20,22,0.3)' },
    gold:   { label: 'Gold',   grad: 'linear-gradient(145deg,#F6E0A8,#D8B65C 48%,#9C7A22)', etch: 'rgba(48,32,0,0.85)', etchOff: 'rgba(48,32,0,0.32)' }
  };

  function css(el, obj) { Object.keys(obj).forEach(function (k) { el.style[k] = obj[k]; }); }
  function stoneImg(s, w) { return s.img ? s.img + '&width=' + (w || 180) : null; }

  /* the design's hardware() — the metal disc gradients for finish swatches + elastic bar */
  function hardware(colorKey, surface) {
    var c = COLORS[colorKey];
    var gloss = surface === 'gloss';
    return {
      name: c.name, etch: c.etch,
      label: c.name + ' · ' + (gloss ? 'Gloss' : 'Matte'),
      barBg: gloss
        ? 'linear-gradient(90deg,' + (colorKey === 'black'
          ? [c.c + ' 0%', c.b + ' 8%', c.b + ' 12%', c.a + ' 19%', c.a + ' 22%', c.spec + ' 27%', c.spec + ' 30%', c.a + ' 36%', c.a + ' 43%', c.b + ' 47%', c.c + ' 52%', c.c + ' 56%', c.b + ' 61%', c.a + ' 70%', c.a + ' 74%', c.spec + ' 79%', c.a + ' 85%', c.b + ' 92%', c.c + ' 100%']
          : [c.c + ' 0%', c.b + ' 7%', c.c + ' 15%', c.c + ' 19%', c.b + ' 27%', c.a + ' 34%', c.spec + ' 45%', c.a + ' 52%', c.spec + ' 60%', c.a + ' 68%', c.b + ' 74%', c.c + ' 81%', c.c + ' 85%', c.b + ' 93%', c.c + ' 100%']).join(',') + ')'
        : 'linear-gradient(90deg,' + c.b + ' 0%,' + c.a + ' 34%,' + c.a + ' 62%,' + c.b + ' 100%)',
      barShadow: gloss
        ? 'inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -2px 4px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.18)'
        : 'inset 0 1px 0 rgba(255,255,255,0.14), 0 2px 5px rgba(0,0,0,0.14)',
      discBg: gloss
        ? ['radial-gradient(ellipse 30% 13% at 32% 21%, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.7) 42%, rgba(255,255,255,0) 78%)',
           'radial-gradient(ellipse 15% 7% at 62% 33%, ' + c.spec + ' 0%, rgba(255,255,255,0) 80%)',
           'linear-gradient(168deg, rgba(0,0,0,0) 34%, rgba(0,0,0,0.34) 51%, rgba(0,0,0,0.06) 62%, rgba(0,0,0,0) 70%)',
           'radial-gradient(ellipse 66% 26% at 52% 97%, ' + c.spec + ' 0%, ' + c.a + ' 32%, rgba(255,255,255,0) 76%)',
           'radial-gradient(circle at 34% 25%, ' + c.a + ' 0%, ' + c.b + ' 46%, ' + c.c + ' 76%, ' + c.b + ' 100%)'].join(',')
        : ['radial-gradient(ellipse 44% 30% at 33% 23%, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.16) 46%, rgba(255,255,255,0) 74%)',
           'radial-gradient(ellipse 68% 28% at 52% 98%, ' + c.a + ' 0%, rgba(255,255,255,0) 72%)',
           'radial-gradient(circle at 36% 27%, ' + c.a + ' 0%, ' + c.b + ' 50%, ' + c.c + ' 90%)'].join(','),
      discShadow: gloss
        ? 'inset 0 -3px 5px rgba(0,0,0,0.42), inset 0 2px 2px rgba(255,255,255,0.6), inset 0 0 0 1px rgba(0,0,0,0.22), 0 1px 3px rgba(0,0,0,0.34)'
        : 'inset 0 -2px 5px rgba(0,0,0,0.3), inset 0 1.5px 2px rgba(255,255,255,0.34), inset 0 0 0 1px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.2)'
    };
  }

  function solveClaspGap(n) {
    var f = function (g) { return CLASP_W_EFF * 2 * Math.PI * (360 - g) - 2 * BEAD_PCT_FIXED * BEAD_PITCH * 360 * n * Math.sin(g * Math.PI / 360); };
    var lo = 1, hi = 90, mid, i;
    for (i = 0; i < 40; i++) { mid = (lo + hi) / 2; if (f(mid) > 0) { lo = mid; } else { hi = mid; } }
    return (lo + hi) / 2;
  }
  function spin(seed) {
    var h = 2166136261, i;
    for (i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619); }
    return ((h >>> 0) % 360) + 'deg';
  }

  /* ⛔ THE CLASP URL MUST CARRY SHOPIFY'S ?v= CACHE KEY. Building it by concatenating a base
     path drops the version, and an unversioned CDN asset is cached FOREVER — which is why three
     rounds of "the MagSnap is still too small" never took: the correct, alpha-cropped PNGs were
     on the theme (verified by pulling them back, byte-identical to local) while every browser
     and the CDN kept serving the ORIGINAL uncropped generation, whose metal fills only 49% of
     its frame. The box measured 2.46 bead-widths; the visible metal was 1.21. Liquid now emits
     all six versioned URLs and we look them up; the concatenation stays only as a fallback. */
  function claspUrl(color, surface, urls, base) {
    var key = color + '-' + (surface === 'matte' ? 'matte' : 'gloss');
    if (urls && urls[key]) { return urls[key]; }
    return (base || '') + 'clasp-' + key + '.png';
  }

  var THREAD_DEFS =
    '<defs>' +
    '<linearGradient id="mzbbSteel" x1="0" y1="0" x2="0.95" y2="1"><stop offset="0" stop-color="#E7EAEE"/><stop offset="0.16" stop-color="#B3B9C0"/><stop offset="0.33" stop-color="#8A9098"/><stop offset="0.48" stop-color="#C3C9CF"/><stop offset="0.63" stop-color="#949AA2"/><stop offset="0.79" stop-color="#B6BCC3"/><stop offset="1" stop-color="#9BA2A9"/></linearGradient>' +
    '<linearGradient id="mzbbCord" x1="0" y1="0" x2="0.9" y2="1"><stop offset="0" stop-color="#FFFFFF" stop-opacity="0.95"/><stop offset="0.3" stop-color="#EDEAE0" stop-opacity="0.74"/><stop offset="0.55" stop-color="#FCFAF5" stop-opacity="0.9"/><stop offset="0.78" stop-color="#E4DFD1" stop-opacity="0.7"/><stop offset="1" stop-color="#F5F2E9" stop-opacity="0.88"/></linearGradient>' +
    '</defs>';

  function drawPreview(el, build, opts) {
    opts = opts || {};
    var st;
    if (typeof build === 'string') {
      var code = build.replace(/^\?/, '');
      st = parseBuild(code);
      delete st._lastCentre;
      /* an EMPTY `d=` parses as one empty bead ("".split gives [""]); a build with no beads draws nothing,
         as the builder does. Only here: decodeBuild keeps its behaviour. */
      if (!/(?:^|&)d=[^&]/.test(code)) { st.beads = []; }
    } else { st = build || {}; }
    var doc = el.ownerDocument || (typeof document !== 'undefined' ? document : null);
    var interactive = !!opts.interactive;
    var decorate = typeof opts.decorate === 'function' ? opts.decorate : null;
    var hl = typeof opts.highlight === 'number' ? opts.highlight : -1;
    var claspAspect = (typeof opts.claspAspect === 'number' && isFinite(opts.claspAspect)) ? opts.claspAspect : CLASP_ASPECT_DEFAULT;
    var beads = st.beads || [];
    var n = beads.length;
    var out = { placed: 0, n: n, beads: [], clasp: null, unknown: st.unknownBeads || 0 };
    el.textContent = '';
    if (!n) { return out; }
    var isMag = st.construction !== 'elastic';
    var showClasp = isMag;
    var gap = showClasp ? solveClaspGap(n) : 0;
    var step = (360 - gap) / n;
    var RP0 = (BEAD_PCT_FIXED * BEAD_PITCH * 360) / (2 * Math.PI * step);
    var fitK = Math.min(1, 45 / (RP0 + BEAD_PCT_FIXED / 2));
    var beadPct = BEAD_PCT_FIXED * fitK;
    var RP = RP0 * fitK;
    var halfGapRad = (gap / 2) * Math.PI / 180;
    var claspW = CLASP_W_EFF * fitK;
    var claspY = 50 + RP * Math.cos(halfGapRad);
    var ci = centreIndices(st, n);
    var cubeIndex = st.cubeOn ? ci.top : -1;
    var charmIndex = st.charm !== 'none' ? ci.top : -1;
    var charmDefn = charmDef(st.charm);
    var fin = hardware(st.color, st.surface);
    var stageW = (opts.stageWidth != null ? opts.stageWidth : el.clientWidth) || 300;

    /* the THREAD — the string the beads sit on; a big part of looking real */
    if (st.construction) {
      var threadFill = isMag ? 'url(#mzbbSteel)' : 'url(#mzbbCord)';
      var threadShade = isMag ? 'rgba(24,27,31,0.38)' : 'rgba(96,88,68,0.5)';
      var threadHi = isMag ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.85)';
      var threadW = isMag ? 0.9 : 0.62;
      var threadShadeW = threadW + (isMag ? 0.24 : 0.52);
      var threadHiW = isMag ? 0.22 : 0.18;
      var threadHiDash = isMag ? '3.2 2' : '1.6 1';
      var threadWeave = isMag ? 'rgba(255,255,255,0.1)' : 'rgba(150,141,118,0.22)';
      var threadWeaveW = isMag ? 0.9 : 0.62;
      var threadWeaveDash = isMag ? '0.34 0.5' : '0.3 0.4';
      var ang1 = (90 + gap / 2) * Math.PI / 180;
      var ang2 = (90 - gap / 2 + 360) * Math.PI / 180;
      var tx1 = 50 + RP * Math.cos(ang1), ty1 = 50 + RP * Math.sin(ang1);
      var tx2 = 50 + RP * Math.cos(ang2), ty2 = 50 + RP * Math.sin(ang2);
      var threadPath = gap > 0.5
        ? 'M ' + tx1 + ' ' + ty1 + ' A ' + RP + ' ' + RP + ' 0 1 1 ' + tx2 + ' ' + ty2
        : 'M ' + (50 + RP) + ' 50 A ' + RP + ' ' + RP + ' 0 1 1 ' + (50 - RP) + ' 50 A ' + RP + ' ' + RP + ' 0 1 1 ' + (50 + RP) + ' 50';
      var svgNS = 'http://www.w3.org/2000/svg';
      var svg = doc.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 100 100');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; pointer-events:none; shape-rendering:geometricPrecision; z-index:2;';
      svg.innerHTML = THREAD_DEFS +
        '<path d="' + threadPath + '" fill="none" stroke="' + threadShade + '" stroke-width="' + threadShadeW + '" stroke-linecap="round"/>' +
        '<path d="' + threadPath + '" fill="none" stroke="' + threadFill + '" stroke-width="' + threadW + '" stroke-linecap="round"/>' +
        '<path d="' + threadPath + '" fill="none" stroke="' + threadWeave + '" stroke-width="' + threadWeaveW + '" stroke-dasharray="' + threadWeaveDash + '" stroke-linecap="butt"/>' +
        '<path d="' + threadPath + '" fill="none" stroke="' + threadHi + '" stroke-width="' + threadHiW + '" stroke-linecap="round" stroke-dasharray="' + threadHiDash + '"/>';
      el.appendChild(svg);
    }

    var placed = 0;
    beads.forEach(function (id, i) {
      var s = id ? CAT.byId[id] : null;
      if (s || i === cubeIndex || i === charmIndex) { placed++; }
      var ang = (90 + gap / 2 + step * (i + 0.5)) * Math.PI / 180;
      var xp = 50 + RP * Math.cos(ang), yp = 50 + RP * Math.sin(ang);
      var isCube = i === cubeIndex, isCharm = i === charmIndex;
      var sizeP = isCharm ? beadPct * 1.15 : beadPct;
      var sel = i === hl;
      var b = doc.createElement(interactive ? 'button' : 'span');
      if (interactive) { b.type = 'button'; }
      b.setAttribute('aria-label', isCharm ? (charmDefn ? charmDefn.name : 'centre piece') : isCube ? 'initials cube' : (s ? s.name : 'empty bead'));
      css(b, {
        position: 'absolute', left: xp + '%', top: yp + '%',
        width: sizeP + '%', height: sizeP + '%', padding: '0', borderRadius: isCube ? '14%' : '50%',
        transform: 'translate(-50%,-50%) scale(' + (sel ? 1.16 : 1) + ')',
        transformOrigin: 'center', transition: 'transform .18s ease, box-shadow .18s ease',
        boxShadow: sel ? SELECT_RING : 'none',
        display: 'block', zIndex: sel ? 6 : (s || isCube || isCharm) ? 3 : 1, cursor: 'pointer', lineHeight: 0,
        border: '0', background: 'none'
      });
      var face = doc.createElement('span');
      if (isCharm) {
        var sil = hardware('silver', st.surface);
        css(face, charmDefn && charmDefn.img
          ? { display: 'block', width: '100%', height: '100%', borderRadius: '50%', background: 'url(' + charmDefn.img + '&width=180) center/100% no-repeat' }
          : { display: 'block', width: '100%', height: '100%', borderRadius: '50%', background: sil.discBg, boxShadow: sil.discShadow });
      } else if (isCube) {
        css(face, {
          display: 'block', width: '100%', height: '100%', borderRadius: '14%',
          background: 'linear-gradient(115deg, rgba(255,255,255,0) 38%, rgba(255,255,255,0.28) 41%, rgba(255,255,255,0.82) 47%, rgba(255,255,255,0.28) 53%, rgba(255,255,255,0) 56%), ' + CUBE_METAL[st.cubeMetal || 'silver'].grad,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -1px 1px rgba(0,0,0,0.4), inset 1px 0 1px rgba(255,255,255,0.55), inset -1px 0 1px rgba(0,0,0,0.16)'
        });
      } else if (s) {
        var rot = 'rotate(' + spin(s.id + '-' + i) + ')';
        css(face, stoneImg(s)
          ? { display: 'block', width: '100%', height: '100%', borderRadius: '50%', background: 'url(' + stoneImg(s) + ') center/100% no-repeat', transform: rot }
          : { display: 'block', width: '100%', height: '100%', borderRadius: '50%',
              background: 'radial-gradient(circle at 33% 27%, ' + s.highlightHex + ', ' + s.baseHex + ' 52%, rgba(0,0,0,0.72) 108%)',
              boxShadow: 'inset -2px -3px 6px rgba(0,0,0,0.4), inset 2px 2px 4px rgba(255,255,255,0.2)', transform: rot });
      } else {
        css(face, {
          display: 'block', width: '100%', height: '100%', borderRadius: '50%',
          background: '#F4F0E2',
          boxShadow: 'inset 0 2px 7px rgba(0,0,0,.09), inset 0 1px 2px rgba(0,0,0,.06), 0 1px 0 rgba(255,255,255,.72)'
        });
      }
      b.appendChild(face);
      var glyph = isCube ? (st.initials || 'AB').toUpperCase() : '';
      if (glyph) {
        var g = doc.createElement('span');
        g.textContent = glyph;
        css(g, {
          position: 'absolute', inset: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: Math.max(7, Math.round(stageW * sizeP / 100 * 0.4)) + 'px', fontWeight: '700', letterSpacing: '0.02em',
          fontFamily: "'Jost','Futura',sans-serif", color: 'rgba(20,20,22,0.82)', pointerEvents: 'none'
        });
        b.appendChild(g);
      }
      if (decorate) { decorate(b, { kind: 'bead', index: i, id: id, isCube: isCube, isCharm: isCharm, highlighted: sel }); }
      el.appendChild(b);
      out.beads.push(b);
    });
    out.placed = placed;

    /* the clasp / elastic bar / dashed placeholder — design's claspStyle verbatim.
       (An elastic build draws no clasp element: showClasp is isMag, exactly as the builder always drew it.) */
    if (showClasp || !st.construction) {
      var c = doc.createElement(interactive ? 'button' : 'span');
      if (interactive) { c.type = 'button'; }
      c.setAttribute('aria-label', 'MagSnap 4 closure');
      var isMagOn = isMag && st.construction;
      var claspStyles = {
        position: 'absolute', left: '50%', top: (claspY - 0.7) + '%', transform: 'translate(-50%,-50%)',
        width: claspW + '%',
        borderRadius: isMag ? '18%' : '100px',
        background: isMagOn ? 'url(' + claspUrl(st.color, st.surface, opts.claspUrls, opts.claspBase) + ') center/contain no-repeat'
          : st.construction ? fin.barBg : '#F4F0E2',
        boxShadow: (st.construction && !isMag) ? fin.barShadow : 'none',
        border: st.construction ? 'none' : '1px dashed #DCD6C0',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4, padding: '0', cursor: 'pointer'
      };
      claspStyles.height = isMagOn
        ? (claspW * claspAspect) + '%'
        : (beadPct * 0.72) + '%';
      css(c, claspStyles);
      if (decorate) { decorate(c, { kind: 'clasp', index: -1, id: null, isCube: false, isCharm: false, highlighted: false }); }
      el.appendChild(c);
      out.clasp = c;
    }
    return out;
  }

  root.MesBraceletCore = {
    tallyBuild: tallyBuild,
    loadCatalogue: loadCatalogue,
    applyRuntime: applyRuntime,
    getPriceAuthority: getPriceAuthority,
    inStockStones: inStockStones,
    applyAvailability: applyAvailability,
    isOn: isOn,
    finishOn: finishOn,
    stoneOk: stoneOk,
    catalogue: CAT,
    sizeRow: sizeRow,
    beadCount: beadCount,
    normalise: normalise,
    centreIndices: centreIndices,
    charmDef: charmDef,
    priceOf: priceOf,
    bucketFor: bucketFor,
    band: band,
    isSymmetric: isSymmetric,
    placeBead: placeBead,
    nextIndex: nextIndex,
    autoFill: autoFill,
    shrinkPairs: shrinkPairs,
    adjustTo: adjustTo,
    encodeBuild: encodeBuild,
    decodeBuild: decodeBuild,
    parseBuild: parseBuild,
    STEP: STEP,
    /* H500: the ring drawing, shared by the builder and Mesmerize OS */
    drawPreview: drawPreview,
    claspUrl: claspUrl,
    hardware: hardware,
    stoneImg: stoneImg,
    COLORS: COLORS,
    CUBE_METAL: CUBE_METAL,
    ACCENT: ACCENT,
    SELECT_RING: SELECT_RING,
    CLASP_ASPECT_DEFAULT: CLASP_ASPECT_DEFAULT
  };
})(typeof window !== 'undefined' ? window : globalThis);
